// Itinerary collections (reusable day templates). Mirrors the legacy
// `/itinerary-collections` REST surface 1-to-1 — the consultant can
// save a planner day as a collection and apply it back onto any
// itinerary day. See legacy `travel_planner_back/src/itinerary-
// collection/`.

import { z } from 'zod';

// ---------------------------------------------------------------
// Properties jsonb
// ---------------------------------------------------------------

// Legacy `properties: { country }` derived from the first source item's
// country / legs / source itinerary at create time. New rows can carry
// other tags later; left open here so adding fields doesn't require a
// schema migration on the consumer side.
export const itineraryCollectionPropertiesSchema = z.object({
  country: z.string().nullable().optional(),
});

export type ItineraryCollectionProperties = z.infer<
  typeof itineraryCollectionPropertiesSchema
>;

// ---------------------------------------------------------------
// Input shapes
// ---------------------------------------------------------------

export const createItineraryCollectionInput = z.object({
  title: z.string().min(1).max(200),
  // Optional free-text note. Blank / whitespace-only is normalised to
  // NULL server-side so "cleared" and "never written" stay one value.
  description: z.string().max(2000).nullish(),
  // Item ids the consultant ticked in the "Save day as collection"
  // modal. Service expands these with parent-id following (children
  // ride along with their parents).
  itemIds: z.array(z.uuid()).min(1),
});

export type CreateItineraryCollectionInput = z.infer<
  typeof createItineraryCollectionInput
>;

// Rename / re-describe an existing collection. Deliberately narrow: the
// stored items are a snapshot taken at create time, so editing here never
// touches `itinerary_collection_item` — and `properties.country`, derived
// from those items, stays untouched too.
//
// Both fields are optional so the caller can PATCH one without echoing the
// other; omitting a field leaves it as-is, while passing `description: null`
// (or blank) clears it.
export const updateItineraryCollectionInput = z.object({
  id: z.uuid(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullish(),
});

export type UpdateItineraryCollectionInput = z.infer<
  typeof updateItineraryCollectionInput
>;

export const findItineraryCollectionsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  // Title ILIKE search. Empty / undefined skips the predicate.
  search: z.string().optional(),
  // Country filter via `properties->>'country' IN (...)`. Each value
  // is matched verbatim against the stored country code/name.
  countries: z.array(z.string()).optional(),
});

export type FindItineraryCollectionsInput = z.infer<
  typeof findItineraryCollectionsInput
>;

export const assignItineraryCollectionInput = z.object({
  collectionId: z.uuid(),
  itineraryId: z.uuid(),
  // Zero-based day index relative to `itinerary.activeVersion.startTime`.
  dayIndex: z.number().int().min(0),
});

export type AssignItineraryCollectionInput = z.infer<
  typeof assignItineraryCollectionInput
>;

export const itineraryCollectionByIdInput = z.object({
  id: z.uuid(),
});

export const removeItineraryCollectionInput = z.object({
  id: z.uuid(),
});

// ---------------------------------------------------------------
// Output shapes
// ---------------------------------------------------------------

// Light list item — what the picker's collections-view card needs.
// Items are NOT inlined here (separate `byId` for the expand-to-show-
// items affordance) to keep the list payload small.
export const itineraryCollectionListItem = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  organizationId: z.uuid(),
  createdById: z.uuid(),
  createdOn: z.string(),
  properties: itineraryCollectionPropertiesSchema,
  itemCount: z.number().int().nonnegative(),
  thumbnail: z.string().nullable(),
});

export type ItineraryCollectionListItem = z.infer<
  typeof itineraryCollectionListItem
>;

export const paginatedItineraryCollections = z.object({
  data: z.array(itineraryCollectionListItem),
  count: z.number().int(),
  countAll: z.number().int(),
});

export type PaginatedItineraryCollections = z.infer<
  typeof paginatedItineraryCollections
>;

// Detail row exposed by `byId` — keeps the small projection on items
// (title / thumbnail / category) since the expand-card just shows a
// preview list, not full editable items.
export const itineraryCollectionItemPreview = z.object({
  id: z.uuid(),
  title: z.unknown(),
  thumbnail: z.string().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  parentId: z.uuid().nullable(),
});

export type ItineraryCollectionItemPreview = z.infer<
  typeof itineraryCollectionItemPreview
>;

export const itineraryCollectionDetail = itineraryCollectionListItem.extend({
  items: z.array(itineraryCollectionItemPreview),
});

export type ItineraryCollectionDetail = z.infer<
  typeof itineraryCollectionDetail
>;
